'use client';

import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { FirebaseError } from 'firebase/app';

import type { CreateSubscriptionUseCase } from '../../domain/usecases/create-subscription';
import type { CancelSubscriptionUseCase } from '../../domain/usecases/cancel-subscription';
import type { PlanBillingPeriod } from '@/core/domain';
import type { ActivateFreePlanUseCase } from '../../domain/usecases/activate-free-plan';
import type {
  CreateSubscriptionCheckoutResponse,
} from '@/core/services/payments/payment-functions.service';

export interface PaymentsState {
  loading: boolean;
  error: string | null;
}

export type CreateSubscriptionOperationResult =
  | { success: true; data: CreateSubscriptionCheckoutResponse }
  | { success: false; error: string };

export type ActivateFreePlanOperationResult =
  | { success: true }
  | { success: false; error: string };

export type CancelSubscriptionOperationResult =
  | { success: true }
  | { success: false; error: string };

export interface PaymentsEvents {
  createSubscription: (
    plan_id: string,
    billing_period: PlanBillingPeriod
  ) => Promise<CreateSubscriptionOperationResult>;
  activateFreePlan: (plan_id: string) => Promise<ActivateFreePlanOperationResult>;
  cancelSubscription: () => Promise<CancelSubscriptionOperationResult>;
  clearError: () => void;
}

export interface PaymentsContextValue {
  state: PaymentsState;
  events: PaymentsEvents;
}

export interface PaymentsProviderProps {
  children: React.ReactNode;
  createSubscriptionUseCase: CreateSubscriptionUseCase;
  activateFreePlanUseCase: ActivateFreePlanUseCase;
  cancelSubscriptionUseCase: CancelSubscriptionUseCase;
}

const initialState: PaymentsState = {
  loading: false,
  error: null,
};

const PaymentsContext = createContext<PaymentsContextValue | null>(null);

export function PaymentsProvider({
  children,
  createSubscriptionUseCase,
  activateFreePlanUseCase,
  cancelSubscriptionUseCase,
}: PaymentsProviderProps) {
  const [state, setState] = useState<PaymentsState>(initialState);

  const debugEnabled = import.meta.env.DEV;
  const debugLog = useCallback(
    (event: string, payload?: unknown) => {
      if (!debugEnabled) return;
      // eslint-disable-next-line no-console
      console.debug(`[PaymentsContext] ${event}`, payload);
    },
    [debugEnabled]
  );

  const clearError = useCallback(() => {
    setState((s) => ({ ...s, error: null }));
  }, []);

  const createSubscription = useCallback(
    async (
      plan_id: string,
      billing_period: PlanBillingPeriod
    ): Promise<CreateSubscriptionOperationResult> => {
      setState((s) => ({ ...s, loading: true, error: null }));
      try {
        const data = await createSubscriptionUseCase.execute({
          plan_id,
          billing_period,
          return_base_url:
            typeof window !== 'undefined' ? window.location.origin : undefined,
        });
        setState((s) => ({ ...s, loading: false }));
        return { success: true, data };
      } catch (e) {
        if (e instanceof FirebaseError) {
          debugLog('createSubscription.error', {
            code: e.code,
            message: e.message,
            name: e.name,
            customData: (e as unknown as { customData?: unknown }).customData ?? null,
          });
        } else if (e instanceof Error) {
          debugLog('createSubscription.error', { name: e.name, message: e.message });
        } else {
          debugLog('createSubscription.error', { raw: String(e) });
        }
        const errorMessage =
          e instanceof FirebaseError ?
            e.message
          : e instanceof Error ?
            e.message
          : String(e);
        setState((s) => ({ ...s, loading: false, error: errorMessage }));
        return { success: false, error: errorMessage };
      }
    },
    [createSubscriptionUseCase, debugLog]
  );

  const activateFreePlan = useCallback(
    async (plan_id: string): Promise<ActivateFreePlanOperationResult> => {
      setState((s) => ({ ...s, loading: true, error: null }));
      try {
        await activateFreePlanUseCase.execute({ plan_id });
        setState((s) => ({ ...s, loading: false }));
        return { success: true };
      } catch (e) {
        if (e instanceof FirebaseError) {
          debugLog('activateFreePlan.error', {
            code: e.code,
            message: e.message,
            name: e.name,
          });
        } else if (e instanceof Error) {
          debugLog('activateFreePlan.error', { name: e.name, message: e.message });
        } else {
          debugLog('activateFreePlan.error', { raw: String(e) });
        }
        const errorMessage =
          e instanceof FirebaseError ?
            e.message
          : e instanceof Error ?
            e.message
          : String(e);
        setState((s) => ({ ...s, loading: false, error: errorMessage }));
        return { success: false, error: errorMessage };
      }
    },
    [activateFreePlanUseCase, debugLog]
  );

  const cancelSubscription = useCallback(async (): Promise<CancelSubscriptionOperationResult> => {
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      await cancelSubscriptionUseCase.execute();
      setState((s) => ({ ...s, loading: false }));
      return { success: true };
    } catch (e) {
      if (e instanceof FirebaseError) {
        debugLog('cancelSubscription.error', {
          code: e.code,
          message: e.message,
        });
      } else if (e instanceof Error) {
        debugLog('cancelSubscription.error', { name: e.name, message: e.message });
      } else {
        debugLog('cancelSubscription.error', { raw: String(e) });
      }
      const errorMessage =
        e instanceof FirebaseError ?
          e.message
        : e instanceof Error ?
          e.message
        : String(e);
      setState((s) => ({ ...s, loading: false, error: errorMessage }));
      return { success: false, error: errorMessage };
    }
  }, [cancelSubscriptionUseCase, debugLog]);

  const events: PaymentsEvents = useMemo(
    () => ({
      createSubscription,
      activateFreePlan,
      cancelSubscription,
      clearError,
    }),
    [createSubscription, activateFreePlan, cancelSubscription, clearError]
  );

  const value: PaymentsContextValue = useMemo(
    () => ({ state, events }),
    [state, events]
  );

  return (
    <PaymentsContext.Provider value={value}>{children}</PaymentsContext.Provider>
  );
}

export function usePayments(): PaymentsContextValue {
  const ctx = useContext(PaymentsContext);
  if (!ctx) {
    throw new Error('usePayments must be used within PaymentsProvider');
  }
  return ctx;
}
